import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  Redirect,
} from 'next';
import { Session, getServerSession } from 'next-auth';
import getConfig from 'next/config';
import { authOptions } from '../../pages/api/auth/[...nextauth]';
import { ParsedUrlQuery } from 'querystring';
import { getTLD } from '../../helpers/domain';
import { PAGE } from '../../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';

const {
  publicRuntimeConfig: { BASE_URL },
} = getConfig();

/**
 * This function will correctly format the Authorization header with
 * @param token the authentication token
 * @returns the formatted Authorization header
 */
const setAuthorizationHeader = (token: string) => {
  return `Bearer ${token}`;
};

export type RedirectResponse = { redirect: Redirect };

const redirect = (destination: string): RedirectResponse => {
  return {
    redirect: {
      destination,
      permanent: false,
    },
  };
};

type AuthGuard = <P extends { [key: string]: any }, Q extends ParsedUrlQuery>(
  cb: (
    context: GetServerSidePropsContext<Q>,
    session?: Session,
  ) => Promise<GetServerSidePropsResult<P>>,
) => GetServerSideProps<P, Q>;

// getServerSideProps function decorator
const authGuard: AuthGuard = (callback) => async (context) => {
  const tld = getTLD(context.req);
  const baseUrl = `${BASE_URL}${tld}`;

  try {
    const session = await getServerSession(
      context.req,
      context.res,
      authOptions,
    );

    const isLoggedInKeycloakUser = !!session;

    if (isLoggedInKeycloakUser) {
      const serverSidePropsResult = await callback(context, session);
      return serverSidePropsResult;
    }

    const redirectUrl = `${baseUrl}/signin?callbackUrl=${baseUrl}${context.req.url}`;
    return redirect(`/signin?callbackUrl=${redirectUrl}`);
  } catch (error) {
    logger.error({
      error,
      route: PAGE.GSSP_WRAPPER,
      message: 'Failed to get server session',
    });
    return redirect(baseUrl);
  }
};

export { authGuard, setAuthorizationHeader };
